import React from 'react';
import { AppBar, Toolbar, IconButton, Typography, Drawer, Divider, List, Theme } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import clsx from 'clsx';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LoginControl from './LoginControl';
import SearchControl from './SearchControl';
import { MainListItems } from './MainListItems';
import LanguageControl from './LanguageControl';
import MobileMenuControl from './MobileMenuControl';

const drawerWidth = 240;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        toolbar: {
            paddingRight: 24, // keep right padding when drawer closed
        },
        toolbarIcon: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: '0 8px',
            ...theme.mixins.toolbar,
        },
        appBar: {
            zIndex: theme.zIndex.drawer + 1,
            transition: theme.transitions.create(['width', 'margin'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
        },
        appBarShift: {
            marginLeft: drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`,
            transition: theme.transitions.create(['width', 'margin'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
        },
        grow: {
            flexGrow: 1,
        },
        menuButton: {
            marginRight: theme.spacing(2),
        },
        title: {
            display: 'none',
            cursor: 'pointer',
            [theme.breakpoints.up('sm')]: {
                display: 'block',
            },
        },
        sectionDesktop: {
            display: 'none',
            [theme.breakpoints.up('md')]: {
                display: 'flex',
            },
        },
        sectionMobile: {
            display: 'flex',
            [theme.breakpoints.up('md')]: {
                display: 'none',
            },
        },
        drawerPaper: {
            position: 'relative',
            whiteSpace: 'nowrap',
            width: drawerWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
        },
        drawerPaperClose: {
            overflowX: 'hidden',
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            width: theme.spacing(7),
            [theme.breakpoints.up('sm')]: {
                width: theme.spacing(9),
            },
        },
    }),
);

function MainAppBar() {
    const [open, setOpen] = React.useState(false);

    const handleDrawerOpen = () => {
        setOpen(!open);
    };
    const handleDrawerClose = () => {
        setOpen(false);
    };

    const [anchorLanguage, setAnchorLanguage] = React.useState<null | HTMLElement>(null);
    const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorLanguage(event.currentTarget);
    };
    const handleLanguageMenuClose = () => {
        setAnchorLanguage(null);
    };

    const [profileAnchorEl, setProfileAnchorEl] = React.useState<null | HTMLElement>(null);

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setProfileAnchorEl(event.currentTarget);
    };
    const handleProfileMenuClose = () => {
        setProfileAnchorEl(null);
    };

    const classes = useStyles();
    const navigate = useNavigate();
    return <>
        <AppBar position="absolute" className={clsx(classes.appBar, open && classes.appBarShift)}>
            <Toolbar className={classes.toolbar}>
                <IconButton
                    edge="start"
                    className={classes.menuButton}
                    color="inherit"
                    aria-label="open drawer"
                    onClick={handleDrawerOpen}
                    size="large">
                    <MenuIcon />
                </IconButton>
                <Typography className={classes.title} variant="h6" noWrap onClick={() => navigate('/')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/'); }} role="button" tabIndex={0}>
                    ontime.bike
                </Typography>

                <SearchControl />
                <div className={classes.grow} />
                <div className={classes.sectionDesktop}>
                    <LanguageControl
                        anchorLanguage={anchorLanguage}
                        handleLanguageMenuOpen={handleLanguageMenuOpen}
                        handleLanguageMenuClose={handleLanguageMenuClose} />
                    <LoginControl
                        profileAnchorEl={profileAnchorEl}
                        handleProfileMenuOpen={handleProfileMenuOpen}
                        handleProfileMenuClose={handleProfileMenuClose} />
                </div>
                <div className={classes.sectionMobile}>
                    <MobileMenuControl
                        handleLanguageMenuOpen={handleLanguageMenuOpen}
                        handleProfileMenuOpen={handleProfileMenuOpen} />
                </div>

            </Toolbar>

        </AppBar>

        <Drawer
            variant="temporary"
            classes={{
                paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose),
            }}
            open={open}
            onClose={handleDrawerClose}
        >
            <div className={classes.toolbarIcon}>
                <IconButton onClick={handleDrawerClose} size="large">
                    <ChevronLeftIcon />
                </IconButton>
            </div>
            <Divider />


            <List><MainListItems /></List>

        </Drawer>
    </>;
}

export default MainAppBar;